import React, { useEffect, useRef, useState } from 'react';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from '../Tool';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../../toolbars/ToolButton';
import WhiteboardController from '../../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import ToolWireframe from './ToolWireframe';
import styles from '../../styles.module.css';
import ToolButtonWithLabel from '../../toolbars/ToolButtonWithLabel';
import { isWireframe } from '../../objects/guards';
import type Wireframe from '../../objects/Wireframe';
import { camelToSentenceCase } from '../../utils/string';
import { WIREFRAME_SELECTION_ERROR_MESSAGE } from '../../utils/tools';
import { isEqual } from 'lodash';

interface PopupProps {
  currentState: string;
  setCurrentState: (state: string) => void;
  selection: Wireframe | fabric.Object;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ currentState, setCurrentState, selection, hidePopup }) => {
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const controller = useController(WhiteboardController);
    const states = selection.wireframe.itemStates();

    const setState = state => {
      setCurrentState(state);
      selection.wireframe.setItemState(state);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        {states.map(state => (
          <ToolButton
            active={state === currentState}
            className={styles.stringToolButton}
            key={state}
            onClick={() => setState(state)}
          >
            {camelToSentenceCase(state)}
          </ToolButton>
        ))}
      </div>
    );
  }
);

const StateToolButton: ToolButtonComponent = ApplicationView(
  ({ selection }) => {
    const tippyRef = useRef<TippyInstance>();
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const initialState = selection.wireframe.activeItemState();
    const [state, setState] = useState(initialState);

    useEffect(() => {
      const handleSelectionModified = () => {
        const newItemState = selection.wireframe.activeItemState();
        if (!isEqual(newItemState, state)) {
          setState(newItemState);
        }
      };

      selection.on('modified', handleSelectionModified);
      return () => {
        selection.off('modified', handleSelectionModified);
      };
    }, [selection, state]);

    return (
      <ToolButtonWithLabel
        popup={
          <Popup
            currentState={state}
            setCurrentState={setState}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        label={'State'}
        tippyRef={tippyRef}
      >
        {camelToSentenceCase(state)}
      </ToolButtonWithLabel>
    );
  }
);

class ToolWireframeItemState extends ToolWireframe {
  static toolName = 'State';

  static available({ selection }: IToolAvailableInput) {
    return selection?.type === 'wireframe';
  }

  static buttonComponent() {
    return StateToolButton;
  }
}

export default ToolWireframeItemState;
